// Desktop workspace for the full-screen builders (KAN-763 / RD-UI-058): the
// form pane (THE scroller, `scrollRef` lands on it) with an OPTIONAL summary
// rail beside it. Reuses Log Job's layout styles by import — nothing copied.
//
// `rail={null}` is a first-class state: the form is ONE centered column
// (`FULLSCREEN_SINGLE_COLUMN_MAX_WIDTH`) and no rail exists in the DOM. When
// `rail` goes null -> node the rail slides in from the right; node -> null it
// slides back out and then unmounts, so KAN-764 / KAN-765 reuse the same
// "step 1 has no rail, later steps do" behavior. Motion: the slot's width and
// opacity tween (the form pane re-centers continuously, never jumps) while the
// rail itself translates 24px -> 0. Nothing here sits inside an AutoHeight, and
// the transform is `none` at rest. Unmounting is driven by a timer, never by
// transitionend alone. Reduced motion: no tween, instant.
import { useEffect, useRef, useState } from 'react';
import { layoutStyles as L, LOGJOB_RAIL_WIDTH } from '../../../pages/logJobLayoutStyles';
import useReducedMotion from '../../../hooks/ai/useReducedMotion';

/** Max width of the form column while no rail is shown (one centered column). */
export const FULLSCREEN_SINGLE_COLUMN_MAX_WIDTH = 720;
/** Rail slide timings (ms). */
export const FULLSCREEN_RAIL_MOTION = { enter: 240, exit: 200 };

const EASE_ENTER = 'cubic-bezier(.2,.8,.2,1)';
const EASE_EXIT = 'ease-in';
const BACKUP_SLACK_MS = 80;
const SLIDE_DISTANCE = '24px';

const slotBase = { flex: '0 0 auto', display: 'flex', minHeight: 0, overflow: 'hidden' };
const railWrap = { flex: '0 0 auto', display: 'flex', minHeight: 0 };

function slotStyle(open) {
  const ms = open ? FULLSCREEN_RAIL_MOTION.enter : FULLSCREEN_RAIL_MOTION.exit;
  const ease = open ? EASE_ENTER : EASE_EXIT;
  return {
    ...slotBase,
    width: open ? `${LOGJOB_RAIL_WIDTH}px` : '0px',
    opacity: open ? 1 : 0,
    transition: `width ${ms}ms ${ease}, opacity ${ms}ms ${ease}`,
  };
}

function railWrapStyle(open) {
  const ms = open ? FULLSCREEN_RAIL_MOTION.enter : FULLSCREEN_RAIL_MOTION.exit;
  const ease = open ? EASE_ENTER : EASE_EXIT;
  return {
    ...railWrap,
    transform: open ? 'none' : `translateX(${SLIDE_DISTANCE})`,
    transition: `transform ${ms}ms ${ease}`,
  };
}

/**
 * @param {object} props
 * @param {import('react').ReactNode} props.form - the step content.
 * @param {import('react').ReactNode} [props.rail] - a FullScreenSummaryRail, or null/undefined for no rail.
 * @param {import('react').Ref<HTMLDivElement>} [props.scrollRef]
 * @param {string} [props.testId]
 */
export default function FullScreenTwoPane({ form, rail = null, scrollRef, testId = 'fullscreen-flow-two-pane' }) {
  const reduced = useReducedMotion();
  const hasRail = rail != null;
  const lastRailRef = useRef(rail);
  if (hasRail) lastRailRef.current = rail;

  // A rail present at first mount is simply there (no entrance).
  const [mounted, setMounted] = useState(hasRail);
  const [shown, setShown] = useState(hasRail);
  const timerRef = useRef(null);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (hasRail) {
      setMounted(true);
      if (reduced) { setShown(true); return undefined; }
      // Let the browser paint the off-screen state the entrance starts from.
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(() => setShown(true)); });
      return () => { cancelAnimationFrame(raf1); if (raf2) cancelAnimationFrame(raf2); };
    }
    setShown(false);
    if (reduced) { setMounted(false); return undefined; }
    timerRef.current = setTimeout(() => setMounted(false), FULLSCREEN_RAIL_MOTION.exit + BACKUP_SLACK_MS);
    return () => clearTimeout(timerRef.current);
  }, [hasRail, reduced]);

  const renderSlot = hasRail || (!reduced && mounted);
  const open = reduced ? hasRail : hasRail && shown;
  const railNode = hasRail ? rail : lastRailRef.current;
  const columnStyle = hasRail
    ? { ...L.column, ...L.columnDesktop, ...L.columnPadDesktop }
    : { ...L.column, maxWidth: `${FULLSCREEN_SINGLE_COLUMN_MAX_WIDTH}px`, ...L.columnPadDesktop };

  return (
    <div style={L.twoPane} data-testid={testId}>
      <div ref={scrollRef} style={L.formPane} data-testid={`${testId}-form`} data-rail={hasRail ? 'on' : 'off'}>
        <div style={columnStyle}>{form}</div>
      </div>
      {renderSlot ? (
        <div
          style={reduced ? { ...slotBase, width: `${LOGJOB_RAIL_WIDTH}px` } : slotStyle(open)}
          data-testid={`${testId}-rail-slot`}
          data-state={open ? 'open' : 'closed'}
          aria-hidden={open ? undefined : true}
          inert={!open}
        >
          <div style={reduced ? railWrap : railWrapStyle(open)}>{railNode}</div>
        </div>
      ) : null}
    </div>
  );
}
